import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { AIChat } from "../shared/AIChat";
import { Logo } from "../shared/Logo";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { X, MessageCircle, Send, Headset, Loader2 } from "lucide-react";

const SID_KEY = "cc_chat_sid";
function getSid() {
  let s = localStorage.getItem(SID_KEY);
  if (!s) {
    s = "web-" + Math.random().toString(36).slice(2, 12);
    localStorage.setItem(SID_KEY, s);
  }
  return s;
}

export const ChatWidget = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("ai"); // ai | human | closed
  const [msgs, setMsgs] = useState([]);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [hName, setHName] = useState("");
  const [hEmail, setHEmail] = useState("");
  const [hMsg, setHMsg] = useState("");
  const [input, setInput] = useState("");
  const [connecting, setConnecting] = useState(false);
  const sidRef = useRef(getSid());
  const bottomRef = useRef(null);

  const fetchHuman = useCallback(async () => {
    try {
      const { data } = await api.get(`/support/chats/${sidRef.current}/messages`);
      if (data.exists) {
        setMsgs(data.messages);
        setMode(data.status === "closed" ? "closed" : "human");
      }
    } catch {}
  }, []);

  useEffect(() => { if (open) fetchHuman(); }, [open, fetchHuman]);

  useEffect(() => {
    if (!open || mode !== "human") return;
    const t = setInterval(fetchHuman, 4000);
    return () => clearInterval(t);
  }, [open, mode, fetchHuman]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, mode]);

  const connectHuman = async () => {
    const name = user?.name || hName.trim();
    const email = user?.email || hEmail.trim();
    if (!name) {
      toast.error("Add your name so the team knows who they're talking to");
      return;
    }
    setConnecting(true);
    try {
      await api.post("/support/handoff", { session_id: sidRef.current, name, email, message: hMsg.trim() });
      setHandoffOpen(false);
      setHMsg("");
      await fetchHuman();
      toast.success("You're connected — a CastleCorp team member will reply right here.");
    } catch {
      toast.error("Couldn't reach the team — please try again.");
    }
    setConnecting(false);
  };

  const requestHuman = () => {
    if (user) {
      setHandoffOpen(false);
      connectHuman();
    } else {
      setHandoffOpen(true);
    }
  };

  const sendHuman = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    setMsgs((m) => [...m, { id: `tmp-${Date.now()}`, from: "customer", content: text, created_at: new Date().toISOString() }]);
    try {
      await api.post(`/support/chats/${sidRef.current}/messages`, { content: text });
    } catch {}
  };

  const subtitle = mode === "human" ? "Live · Human support" : mode === "closed" ? "Conversation closed" : "Live · AI + human support";

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-5 z-[60] w-[370px] max-w-[calc(100vw-40px)] h-[500px] rounded-2xl border bg-card shadow-2xl flex flex-col overflow-hidden fade-up" data-testid="chat-widget-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-secondary/50 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <Logo size={20} />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-card pulse-dot" data-testid="chat-live-dot" />
              </div>
              <div>
                <p className="text-sm font-heading font-semibold leading-none">{mode === "human" ? "CastleCorp Team" : "CastleCorp AI"}</p>
                <p className="text-[10px] mt-1 font-mono-cc uppercase tracking-wider flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${mode === "closed" ? "bg-slate-400" : "bg-emerald-500 pulse-dot"}`} />
                  <span className="text-muted-foreground">{subtitle}</span>
                </p>
              </div>
            </div>
            <button data-testid="chat-widget-close" onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-secondary" aria-label="Close chat">
              <X className="w-4 h-4" />
            </button>
          </div>

          {mode === "ai" && !handoffOpen && (
            <>
              <AIChat mode="customer" sessionId={sidRef.current} className="flex-1 min-h-0"
                intro="Welcome to CastleCorp. I can help with services, availability, or getting your job estimated and booked. What do you need moved, delivered, assembled, or removed?" />
              <button data-testid="chat-talk-to-human" onClick={requestHuman}
                className="shrink-0 flex items-center justify-center gap-2 py-2.5 border-t text-xs font-semibold text-muted-foreground hover:text-[var(--cc-accent)] hover:bg-secondary/50 transition-colors duration-200">
                {connecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Headset className="w-3.5 h-3.5" />} Talk to a human
              </button>
            </>
          )}

          {mode === "ai" && handoffOpen && (
            <div className="flex-1 min-h-0 overflow-y-auto p-5 fade-up" data-testid="chat-handoff-form">
              <Headset className="w-8 h-8 text-[var(--cc-accent)]" />
              <p className="font-heading font-semibold mt-3">Connect with our team</p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">A real CastleCorp team member will take over this chat and reply right here.</p>
              <div className="space-y-3 mt-5">
                <Input data-testid="chat-handoff-name" value={hName} onChange={(e) => setHName(e.target.value)} placeholder="Your name" />
                <Input data-testid="chat-handoff-email" type="email" value={hEmail} onChange={(e) => setHEmail(e.target.value)} placeholder="Email (optional — for follow-up)" />
                <Input data-testid="chat-handoff-message" value={hMsg} onChange={(e) => setHMsg(e.target.value)} placeholder="What do you need help with?" />
                <Button data-testid="chat-handoff-connect" onClick={connectHuman} disabled={connecting}
                  className="w-full rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">
                  {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect Me"}
                </Button>
                <button data-testid="chat-handoff-cancel" onClick={() => setHandoffOpen(false)} className="w-full text-xs text-muted-foreground hover:text-foreground py-1">
                  Back to AI chat
                </button>
              </div>
            </div>
          )}

          {(mode === "human" || mode === "closed") && (
            <>
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3" data-testid="chat-human-messages">
                <div className="rounded-lg bg-secondary/60 border border-dashed px-3 py-2 text-[11px] text-muted-foreground text-center">
                  {mode === "human" ? "You're in the human support queue — the team replies right here." : "This conversation has ended."}
                </div>
                {msgs.map((m) => (
                  m.from === "system" ? (
                    <p key={m.id} className="text-[11px] text-muted-foreground text-center italic py-1">{m.content}</p>
                  ) : (
                    <div key={m.id} className={`flex ${m.from === "customer" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                        m.from === "customer" ? "bg-[var(--cc-accent)] text-black font-medium"
                          : m.from === "agent" ? "bg-secondary border text-foreground"
                          : "bg-secondary/50 border border-dashed text-muted-foreground"}`}>
                        {m.from === "agent" && <p className="text-[10px] font-mono-cc uppercase tracking-wider text-[var(--cc-accent)] mb-1">{m.agent_name || "CastleCorp Team"}</p>}
                        {m.from === "ai" && <p className="text-[10px] font-mono-cc uppercase tracking-wider text-muted-foreground mb-1">AI Assistant</p>}
                        {m.content}
                      </div>
                    </div>
                  )
                ))}
                <div ref={bottomRef} />
              </div>
              {mode === "human" ? (
                <div className="border-t p-3 flex gap-2 shrink-0">
                  <Input data-testid="chat-human-input" value={input} onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendHuman()} placeholder="Message the team..." />
                  <Button data-testid="chat-human-send" onClick={sendHuman} disabled={!input.trim()} size="icon" className="btn-glow shrink-0" aria-label="Send">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="border-t p-3 shrink-0">
                  <Button data-testid="chat-back-to-ai" variant="outline" className="w-full rounded-full" onClick={() => setMode("ai")}>
                    Chat with AI
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Chat with us — AI and human support"
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-[var(--cc-accent)] text-black flex items-center justify-center shadow-lg btn-glow"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-background pulse-dot" data-testid="chat-toggle-live-dot" />
      </button>
    </>
  );
};
